'use client';

import { Language, Subsidiary } from '@coolblue-development/constants';
import { Box } from '@coolblue-development/freddy';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { buildLanguageOptions } from '../buildLanguageOptions';
import { CountryLanguageOptions } from '../constants';
import { LanguageOption } from '../LanguageOption';
import { SubsidiaryOption } from '../SubsidiaryOption';

type Props = {
  allLanguageOptions: Partial<CountryLanguageOptions>;
  isVacancyPage: boolean;
  pageLanguage: Language;
  pageSubsidiary: Subsidiary;
  isHamburgerVariant: boolean;
  showAllLanguageOptions?: boolean;
  setShowAllLanguageOptions?: (showAllLanguageOptions: boolean) => void;
};

export const LanguageOptionsList = ({
  allLanguageOptions: initialLanguageOptions,
  isVacancyPage: initialIsVacancyPage,
  pageLanguage,
  pageSubsidiary,
  isHamburgerVariant,
  showAllLanguageOptions = true,
  setShowAllLanguageOptions
}: Props) => {
  const pathname = usePathname();
  const [allLanguageOptions, setAllLanguageOptions] = useState(initialLanguageOptions);
  const [isVacancyPage, setIsVacancyPage] = useState(initialIsVacancyPage);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip on initial mount to avoid hydration mismatch
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Refetch language options when route changes
    buildLanguageOptions().then(data => {
      setAllLanguageOptions(data.allLanguageOptions);
      setIsVacancyPage(data.isVacancyPage);
    });
  }, [pathname]);

  const filteredLanguageOptions = showAllLanguageOptions
    ? allLanguageOptions
    : { [pageSubsidiary]: allLanguageOptions[pageSubsidiary] };

  const languagePickerOptions = Object.entries(filteredLanguageOptions).map(
    ([subsidiary, options = []]) => {
      return (
        <Box key={subsidiary}>
          <SubsidiaryOption
            subsidiary={subsidiary as Subsidiary}
            pageSubsidiary={pageSubsidiary}
            pageLanguage={pageLanguage}
            isVacancyPage={isVacancyPage}
            showAllLanguageOptions={showAllLanguageOptions}
            setShowAllLanguageOptions={setShowAllLanguageOptions}
          />
          {options.map(langOption => {
            const isEnabled =
              !isVacancyPage || langOption.slug || langOption.language === pageLanguage;
            const isSelected =
              langOption.language === pageLanguage && subsidiary === pageSubsidiary;
            return (
              <LanguageOption
                key={`${subsidiary}-${langOption.language}`}
                selectedLanguage={langOption.language}
                languageOptionText={langOption.languageOptionText || ''}
                slug={langOption.slug}
                isDisabled={!isEnabled}
                isHamburgerVariant={isHamburgerVariant}
                isSelected={isSelected}
                selectedSubsidiary={subsidiary as Subsidiary}
                isVacancyPage={isVacancyPage}
              />
            );
          })}
        </Box>
      );
    }
  );

  return <>{languagePickerOptions}</>;
};
